import { DOCUMENT, inject, Injectable } from '@angular/core';
import { BerufeService } from '../../../../suche-lib/src/lib/services/suchseite/berufe.service';
import { firstValueFrom } from 'rxjs';
import { SuchwortService } from 'projects/suche-lib/src/lib/services/suchseite/suchwort.service';
import { OrtService } from 'projects/suche-lib/src/lib/services/suchseite/ort.service';

/**
 * Service zum Hinterlegen der kanonischen URL zur Seite und zum Hinterlegen im Dokument.
 */
@Injectable({
  providedIn: 'root'
})
export class CanonicalUrlService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly berufeService = inject(BerufeService);

  /**
   * Entfernt, sofern vorhanden, den Link-Tag, der die kanonische URL angibt.
   */
  private removeCanonicalUrl(): void {
    const link = this.document.head.querySelector('#canonical-url');
    if (link) {
      link.remove();
    }
  }

  /**
   * Generiert die aktuelle kanonische URL und fügt den entsprechenden Link-Tag in den Header ein.
   */
  public updateCanonicalUrl(): void {
    this.removeCanonicalUrl();

    const link = this.document.createElement('link');
    this.getCanonicalUrl().then((href) => {
      link.rel = 'canonical';
      link.id = 'canonical-url';
      link.href = href;
      this.document.head.append(link);
    });
  }

  public async getCanonicalUrl(): Promise<string> {
    return this.toCanonicalUrl(new URL(this.document.location.href));
  }

  /**
   * Erzeuge kanonische URL durch Reduktion der Parameter.
   *
   * @param url URL als URL-Objekt
   */
  async toCanonicalUrl(url: URL): Promise<string> {
    // Sonderbehandlung Suchparameter auf der Ergebnisseite bleiben teilweise erhalten
    if (
      url.pathname.endsWith('/weiterbildungssuche/') || // prod
      url.pathname.endsWith('/now-suche/') || // dev
      url.pathname == '/' // local
    ) {
      //whitelist initialisieren
      let params = [
        SuchwortService.URL_PARAM_SUCHWORT,
        OrtService.URL_PARAM_ORT,
        OrtService.URL_PARAM_UMKREIS
      ]
        // reduzieren auf die in der URL vorhandenen
        .filter((param) => url.searchParams.has(param))
        .map((param) => `${param}=${url.searchParams.get(param)}`)
        .join('&');

      // Abbildung von DKZ-ID-Einsprüngen auf Bezeichnung des ersten Treffers, z.B. von ?berufe=3626 auf ?sw=Bäcker/in
      if (await firstValueFrom(this.berufeService.isBerufssuche$)) {
        //Wenn bereits Parameter existieren ein & hinten anhängen
        params = params ? params + '&' : params;
        //Suchwort Parameter mit Berufsbezeichnung befüllen
        params += `${SuchwortService.URL_PARAM_SUCHWORT}=${await firstValueFrom(this.berufeService.bezeichnung$)}`;
      }

      url.search = '?' + params;
    }
    // für alle anderen Seiten werden alle Suchparameter aus der kanonischen URL entfernt
    else {
      url.search = '';
    }

    return url.toString();
  }
}
